/**
 * Feuille de brouillon — le tableau de signes (moteur v42, lot 6.19) et le tableau de
 * variations (lot 6.20 : une ligne de plus, celle de f — flèches, valeurs de f).
 *
 * Une ligne « tableau » est UN bout (« ⊞{…} », `@jeraisonne/reasoner`) : l'élève le
 * remplit dans l'éditeur (valeurs de x dans l'ordre, un signe par intervalle, 0 et
 * double barre, puis les flèches et les valeurs de f), puis il est posé seul sur la ligne
 * et jugé case par case par le moteur. Toucher le tableau de la ligne en cours le rouvre.
 *
 * `SignTableView` : le tableau en lecture (feuille, aperçu) ; `SignTableEditor` :
 * l'éditeur plein écran ; `SignTableHost` : l'éditeur ouvert pour une feuille.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Delete, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  commit,
  createState,
  isSignTableText,
  judge,
  lex,
  parseSignTable,
  signTableSkeleton,
  signTableText,
  type SignCell,
  type SignTable,
  type SignTableSkeleton,
  type VarRow,
} from '@jeraisonne/reasoner';
import { AtomsMath } from './math-view';
import { engineChips, isFrac, type LineItem } from './line-items';
import type { ScratchSession } from './scratch-session';
import { vibrate } from './use-long-press';

/** Le tableau d'une ligne (un seul bout « tableau ») ; null sinon. */
export function signTableOfItems(items: LineItem[]): { text: string; table: SignTable } | null {
  if (items.length !== 1) return null;
  const it = items[0]!;
  if (isFrac(it) || it.atoms.length !== 1 || !isSignTableText(it.atoms[0]!)) return null;
  const table = parseSignTable(it.atoms[0]!);
  return table ? { text: it.atoms[0]!, table } : null;
}

/** Une écriture mathématique (étiquette, valeur de x) ; le texte tel quel s'il ne se lit pas. */
function MathText({ text }: { text: string }) {
  let atoms: string[];
  try {
    atoms = lex(text);
  } catch {
    return <span className="sc-mx">{text}</span>;
  }
  return <AtomsMath atoms={atoms} />;
}

/** La case « valeur » : un trait vertical, le 0 ou la double barre par-dessus. */
function ValueMark({ cell }: { cell: SignCell }) {
  if (cell === '‖')
    return (
      <span className="sc-st-bar" data-double="" aria-hidden>
        <i />
        <i />
      </span>
    );
  return (
    <span className="sc-st-bar" aria-hidden>
      <i />
      {cell === '0' && <b className="sc-st-zero">0</b>}
    </span>
  );
}

const CELL_WORD: Record<SignCell, string> = {
  '+': 'plus',
  '−': 'moins',
  '0': 'zéro',
  '‖': 'valeur interdite',
  '': 'vide',
};

interface Bad {
  row: number;
  col?: number;
}

function gridColumns(n: number, compact = false): string {
  // étiquettes, puis intervalle / valeur / intervalle… (cases de 44 px dans l'éditeur)
  const cell = compact ? '1.75rem' : '2.75rem';
  const parts = [compact ? 'max-content' : 'minmax(4.5rem, max-content)'];
  for (let i = 0; i < 2 * n + 1; i++)
    parts.push(i % 2 === 0 ? `minmax(${cell}, 1fr)` : `minmax(${cell}, auto)`);
  return parts.join(' ');
}

function HeaderRow({ x, values, bad }: { x: string; values: string[]; bad?: Bad }) {
  const n = values.length;
  const cells: ReactNode[] = [
    <div key="l" className="sc-st-label sc-st-head" role="rowheader">
      <MathText text={x} />
    </div>,
  ];
  for (let c = 0; c < 2 * n + 1; c++) {
    if (c % 2 === 1) {
      const i = c >> 1;
      cells.push(
        <div
          key={c}
          className="sc-st-head sc-st-val"
          data-bad={bad?.row === -1 && bad.col === i ? '' : undefined}
          role="columnheader"
        >
          <MathText text={values[i]!} />
        </div>,
      );
      continue;
    }
    cells.push(
      <div key={c} className="sc-st-head sc-st-gap" role="columnheader">
        {c === 0 && <span className="sc-st-inf">−∞</span>}
        {c === 2 * n && <span className="sc-st-inf sc-st-inf-r">+∞</span>}
      </div>,
    );
  }
  return (
    <div className="contents" role="row">
      {cells}
    </div>
  );
}

/** Une case de la ligne de f : une flèche, une valeur de f, la double barre. */
function VarMark({ cell, value }: { cell: string; value: boolean }) {
  if (!value) return cell ? <span className="sc-st-arrow">{cell}</span> : null;
  if (cell === '‖') return <ValueMark cell="‖" />;
  return (
    <span className="sc-st-fval">
      <span className="sc-st-bar" aria-hidden>
        <i />
      </span>
      {cell && (
        <b className="sc-st-fnum">
          <MathText text={cell} />
        </b>
      )}
    </span>
  );
}

const varWord = (cell: string, value: boolean): string =>
  value
    ? cell === '‖'
      ? 'valeur interdite'
      : cell || 'vide'
    : cell === '↗'
      ? 'croissante'
      : cell === '↘'
        ? 'décroissante'
        : 'vide';

/** La ligne de f d'un tableau de variations (lecture). */
function VarRowView({ row, ri, bad }: { row: VarRow; ri: number; bad?: Bad }) {
  return (
    <div className="contents" role="row">
      <div className="sc-st-label" role="rowheader" data-var="">
        <MathText text={row.label} />
      </div>
      {row.cells.map((cell, c) => (
        <div
          key={c}
          role="cell"
          className={cn('sc-st-cell', c % 2 === 1 && 'sc-st-val')}
          data-var=""
          data-arrow={c % 2 === 0 && cell ? (cell === '↗' ? 'up' : 'down') : undefined}
          data-bad={bad?.row === ri && bad.col === c ? '' : undefined}
          aria-label={varWord(cell, c % 2 === 1)}
        >
          <VarMark cell={cell} value={c % 2 === 1} />
        </div>
      ))}
    </div>
  );
}

/** Le tableau en lecture (une ligne de la feuille). */
export function SignTableView({
  table,
  x = 'x',
  bad,
  compact,
}: {
  table: SignTable;
  x?: string;
  bad?: Bad;
  compact?: boolean;
}) {
  const n = table.values.length;
  return (
    <div
      className="sc-st"
      data-compact={compact ? '' : undefined}
      role="table"
      aria-label={
        table.f
          ? `Tableau de variations : ${table.f.label}`
          : `Tableau de signes : ${table.rows[table.rows.length - 1]?.label ?? ''}`
      }
      style={{ gridTemplateColumns: gridColumns(n, compact) }}
    >
      <HeaderRow x={x} values={table.values} bad={bad} />
      {table.rows.map((r, ri) => (
        <div key={ri} className="contents" role="row">
          <div
            className="sc-st-label"
            role="rowheader"
            data-last={ri === table.rows.length - 1 ? '' : undefined}
          >
            <MathText text={r.label} />
          </div>
          {r.cells.map((cell, c) => (
            <div
              key={c}
              role="cell"
              className={cn('sc-st-cell', c % 2 === 1 && 'sc-st-val')}
              data-sign={cell || undefined}
              data-last={ri === table.rows.length - 1 ? '' : undefined}
              data-bad={bad?.row === ri && bad.col === c ? '' : undefined}
              aria-label={CELL_WORD[cell]}
            >
              {c % 2 === 1 ? <ValueMark cell={cell} /> : cell}
            </div>
          ))}
        </div>
      ))}
      {table.f && <VarRowView row={table.f} ri={table.rows.length} bad={bad} />}
    </div>
  );
}

/* ------------------------------ éditeur ------------------------------ */

const NEXT_SIGN: Record<SignCell, SignCell> = { '': '+', '+': '−', '−': '', '0': '', '‖': '' };

function emptyRows(labels: string[], n: number): SignTable['rows'] {
  return labels.map((label) => ({ label, cells: Array<SignCell>(2 * n + 1).fill('') }));
}

/**
 * Tout intervalle a un signe (et une flèche, sur la ligne de f) : le tableau peut aller
 * sur la feuille. Sans valeur de x (f′ de signe constant), une case par ligne.
 */
export function signTableComplete(t: SignTable): boolean {
  const filled = (cells: readonly string[]) => cells.every((c, i) => i % 2 === 1 || c !== '');
  return t.rows.every((r) => filled(r.cells)) && (!t.f || filled(t.f.cells));
}

const NEXT_ARROW: Record<string, string> = { '': '↗', '↗': '↘', '↘': '' };

const emptyVar = (label: string, n: number): VarRow => ({
  label,
  cells: Array<string>(2 * n + 1).fill(''),
});

export function SignTableEditor({
  skeleton,
  initial,
  bad,
  onCancel,
  onSubmit,
}: {
  skeleton: SignTableSkeleton;
  initial?: SignTable | null;
  bad?: Bad;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const variation = skeleton.variation ?? null;
  const start =
    initial &&
    initial.rows.length === skeleton.labels.length &&
    initial.rows.every((r, i) => r.label === skeleton.labels[i]) &&
    !!initial.f === !!variation
      ? initial
      : null;
  const [values, setValues] = useState<string[]>(start?.values ?? []);
  const [rows, setRows] = useState<SignTable['rows']>(start?.rows ?? emptyRows(skeleton.labels, 0));
  // tableau de variations (lot 6.20) : la ligne de f, la case de valeur choisie
  const [fRow, setFRow] = useState<VarRow | null>(
    variation ? (start?.f ?? emptyVar(variation.label, start?.values.length ?? 0)) : null,
  );
  const [pick, setPick] = useState<number | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const lastRow = rows.length - 1;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cancelRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey, true);
      back?.focus?.();
    };
  }, []);

  const setValueList = (next: string[]) => {
    setValues(next);
    // une valeur de plus ou de moins : les cases repartent de zéro
    setRows(emptyRows(skeleton.labels, next.length));
    if (variation) setFRow(emptyVar(variation.label, next.length));
    setPick(null);
  };
  const setVar = (c: number, cell: string) =>
    setFRow((r) => (r ? { ...r, cells: r.cells.map((x, i) => (i === c ? cell : x)) } : r));
  const tapVar = (c: number) => {
    vibrate(6);
    if (c % 2 === 0) {
      setVar(c, NEXT_ARROW[fRow?.cells[c] ?? ''] ?? '');
      setPick(null);
      return;
    }
    // une valeur : f connue → on choisit parmi les valeurs proposées ; sinon vide / ‖
    if (variation?.values) setPick((p) => (p === c ? null : c));
    else setVar(c, fRow?.cells[c] === '‖' ? '' : '‖');
  };
  const tap = (r: number, c: number) => {
    vibrate(6);
    setRows((rs) =>
      rs.map((row, ri) => {
        if (ri !== r) return row;
        const cells = [...row.cells];
        const cur = cells[c]!;
        if (c % 2 === 0) cells[c] = NEXT_SIGN[cur];
        // valeur : 0 sur un facteur ; 0 puis double barre sur la dernière ligne
        else if (r === lastRow) cells[c] = cur === '' ? '0' : cur === '0' ? '‖' : '';
        else cells[c] = cur === '' ? '0' : '';
        return { ...row, cells };
      }),
    );
  };
  const table: SignTable = { values, rows, ...(fRow ? { f: fRow } : {}) };
  const complete = signTableComplete(table);
  const title = variation ? 'Tableau de variations' : 'Tableau de signes';
  const free = skeleton.candidates.filter((c) => !values.includes(c));
  const n = values.length;

  return createPortal(
    <div className="sc-root fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-4">
      <div className="sc-scrim absolute inset-0" onClick={onCancel} aria-hidden />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-table-title"
        tabIndex={-1}
        data-testid="sign-table-editor"
        className="sc-sheet native:max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] relative flex max-h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl outline-none md:w-[92vw] md:max-w-[720px] md:rounded-3xl"
      >
        <header className="flex flex-none items-start gap-2 pb-1 pl-4 pr-2 pt-3 md:pl-5">
          <div className="min-w-0 flex-1">
            <h2 id="sign-table-title" className="font-display text-[17px] font-bold md:text-lg">
              {title}
            </h2>
            <p className="mt-0.5 text-sm text-[var(--sc-ink-2)]">
              de <MathText text={skeleton.subject} />
            </p>
          </div>
          <button
            type="button"
            className="sc-icon-btn"
            aria-label="Fermer le tableau sans le poser"
            onClick={onCancel}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 md:px-5">
          <p className="mt-2 text-sm font-semibold text-[var(--sc-ink)]">
            1. Les valeurs de {skeleton.x}, dans l’ordre
          </p>
          <p className="text-xs text-[var(--sc-muted)]">
            {variation
              ? 'Celles où f′ s’annule ou n’existe pas. Touche-les de la plus petite à la plus grande (aucune si rien ne s’annule).'
              : 'Celles où un facteur s’annule. Touche-les de la plus petite à la plus grande.'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2" aria-label="Valeurs proposées">
            {free.map((c) => (
              <button
                key={c}
                type="button"
                className="sc-chip"
                data-kind="num"
                aria-label={`Placer ${c} sur la ligne des ${skeleton.x}`}
                disabled={n >= 6}
                onClick={() => {
                  vibrate(8);
                  setValueList([...values, c]);
                }}
              >
                <MathText text={c} />
              </button>
            ))}
            <button
              type="button"
              className="sc-chip"
              data-kind="op"
              aria-label="Retirer la dernière valeur"
              disabled={!n}
              onClick={() => {
                vibrate(8);
                setValueList(values.slice(0, -1));
              }}
            >
              <Delete className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <p className="mt-4 text-sm font-semibold text-[var(--sc-ink)]">2. Les signes</p>
          <p className="text-xs text-[var(--sc-muted)]">
            Touche une case pour écrire + ou −. Sous une valeur : 0 si le facteur s’annule, et sur
            la dernière ligne la double barre ‖ si la valeur est interdite.
          </p>
          <div className="mt-2 overflow-x-auto pb-1">
            <div
              className="sc-st sc-st-edit"
              role="table"
              aria-label={`${title} à remplir`}
              style={{ gridTemplateColumns: gridColumns(n) }}
            >
              <HeaderRow x={skeleton.x} values={values} bad={bad} />
              {rows.map((r, ri) => (
                <div key={r.label} className="contents" role="row">
                  <div
                    className="sc-st-label"
                    role="rowheader"
                    data-last={ri === lastRow ? '' : undefined}
                  >
                    <MathText text={r.label} />
                  </div>
                  {r.cells.map((cell, c) => (
                    <button
                      key={c}
                      type="button"
                      role="cell"
                      className={cn('sc-st-cell sc-st-tap', c % 2 === 1 && 'sc-st-val')}
                      data-sign={cell || undefined}
                      data-last={ri === lastRow ? '' : undefined}
                      data-bad={bad?.row === ri && bad.col === c ? '' : undefined}
                      aria-label={`${r.label}, ${
                        c % 2 === 0
                          ? `intervalle ${(c >> 1) + 1}`
                          : `${skeleton.x} = ${values[c >> 1]}`
                      } : ${CELL_WORD[cell]}`}
                      onClick={() => tap(ri, c)}
                    >
                      {c % 2 === 1 ? <ValueMark cell={cell} /> : cell}
                    </button>
                  ))}
                </div>
              ))}
              {fRow && (
                <div className="contents" role="row">
                  <div className="sc-st-label" role="rowheader" data-var="">
                    <MathText text={fRow.label} />
                  </div>
                  {fRow.cells.map((cell, c) => (
                    <button
                      key={c}
                      type="button"
                      role="cell"
                      className={cn('sc-st-cell sc-st-tap', c % 2 === 1 && 'sc-st-val')}
                      data-var=""
                      data-arrow={c % 2 === 0 && cell ? (cell === '↗' ? 'up' : 'down') : undefined}
                      data-picked={pick === c ? '' : undefined}
                      data-bad={bad?.row === rows.length && bad.col === c ? '' : undefined}
                      aria-label={`${fRow.label}, ${
                        c % 2 === 0
                          ? `intervalle ${(c >> 1) + 1}`
                          : `${skeleton.x} = ${values[c >> 1]}`
                      } : ${varWord(cell, c % 2 === 1)}`}
                      onClick={() => tapVar(c)}
                    >
                      <VarMark cell={cell} value={c % 2 === 1} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {variation && (
            <>
              <p className="mt-4 text-sm font-semibold text-[var(--sc-ink)]">
                3. Les variations de {variation.label.replace(/\(.*\)$/, '')}
              </p>
              <p className="text-xs text-[var(--sc-muted)]">
                Touche un intervalle pour la flèche (↗ si {variation.label.replace(/\(.*\)$/, '')}′
                est positive, ↘ si elle est négative).
                {variation.values
                  ? ' Sous une valeur de x, touche la case puis choisis la valeur de f ; ‖ sous une valeur interdite.'
                  : ' Sous une valeur interdite, touche la case pour la double barre ‖.'}
              </p>
              {pick !== null && variation.values && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-2"
                  aria-label={`Valeur de ${variation.label} en ${skeleton.x} = ${values[pick >> 1]}`}
                  data-testid="var-value-picker"
                >
                  {[...variation.candidates, '‖'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="sc-chip"
                      data-kind={v === '‖' ? 'op' : 'num'}
                      aria-label={
                        v === '‖'
                          ? 'Valeur interdite'
                          : `Écrire ${v} sous ${skeleton.x} = ${values[pick >> 1]}`
                      }
                      onClick={() => {
                        vibrate(8);
                        setVar(pick, v);
                        setPick(null);
                      }}
                    >
                      {v === '‖' ? '‖' : <MathText text={v} />}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="sc-chip"
                    data-kind="op"
                    aria-label="Effacer la valeur"
                    onClick={() => {
                      vibrate(8);
                      setVar(pick, '');
                      setPick(null);
                    }}
                  >
                    <Delete className="h-5 w-5" aria-hidden />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex flex-none flex-col-reverse gap-2 border-t border-[var(--sc-line)] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end md:px-5">
          <button
            type="button"
            className="sc-mini-btn w-full sm:w-auto"
            data-ghost=""
            onClick={onCancel}
          >
            Annuler
          </button>
          <button
            type="button"
            className="sc-mini-btn w-full sm:w-auto"
            disabled={!complete}
            onClick={() => onSubmit(signTableText(table))}
          >
            Mettre dans ma feuille
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

/* ------------------------------ ouverture ------------------------------ */

interface Request {
  session: ScratchSession;
  /** le tableau de la ligne en cours, à reprendre */
  initial: string | null;
}
let request: Request | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());

export function openSignTableEditor(session: ScratchSession, initial: string | null = null): void {
  request = { session, initial };
  emit();
}
export function closeSignTableEditor(): void {
  request = null;
  emit();
}
function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/**
 * Ce que l'éditeur propose : rejoue les lignes de la feuille sur un état neuf du moteur
 * (l'état de la feuille reste privé à la session).
 */
export function skeletonOf(session: ScratchSession): SignTableSkeleton | null {
  const st = createState(session.model);
  for (const l of session.getSnapshot().lines) {
    try {
      const v = judge(st, engineChips(l.items), { verify: true });
      if (v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless') commit(st, v);
    } catch {
      /* ligne que le moteur ne relit plus */
    }
  }
  return signTableSkeleton(st);
}

/** L'éditeur ouvert pour cette feuille (monté par la palette). */
export function SignTableHost({ session }: { session: ScratchSession }) {
  const req = useSyncExternalStore(subscribe, () =>
    request?.session === session ? request : null,
  );
  const skeleton = useMemo(() => (req ? skeletonOf(session) : null), [req, session]);
  if (!req) return null;
  if (!skeleton) {
    return (
      <NoTable
        onClose={closeSignTableEditor}
        text={
          session.model.kernel?.plugin === 'variations'
            ? 'Le tableau de variations se lit sur le signe de la dérivée : dérive la fonction, puis écris sa dérivée comme un produit (ou un quotient) de facteurs dont tu connais le signe.'
            : 'Le tableau de signes se dresse sur une ligne comparée à 0 dont chaque facteur a un signe connu. Mets tout d’un côté, puis factorise (ou mets au même dénominateur).'
        }
      />
    );
  }
  const initial = req.initial ? parseSignTable(req.initial) : null;
  const v = session.getSnapshot().verdict;
  const bad = req.initial && v.atoms[0] === req.initial ? v.cell : undefined;
  return (
    <SignTableEditor
      skeleton={skeleton}
      initial={initial}
      bad={bad}
      onCancel={closeSignTableEditor}
      onSubmit={(text) => {
        closeSignTableEditor();
        session.clearLine();
        session.addChip({
          atoms: [text],
          text: skeleton.variation ? 'Tableau de variations' : 'Tableau de signes',
        });
      }}
    />
  );
}

function NoTable({ text, onClose }: { text: string; onClose: () => void }) {
  return createPortal(
    <div className="sc-root fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-4">
      <div className="sc-scrim absolute inset-0" onClick={onClose} aria-hidden />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-table-none"
        data-testid="sign-table-none"
        className="sc-sheet relative w-full rounded-t-3xl p-5 md:max-w-[480px] md:rounded-3xl"
      >
        <h2 id="sign-table-none" className="font-display text-[17px] font-bold">
          Pas encore de tableau
        </h2>
        <p className="mt-2 text-sm text-[var(--sc-ink-2)]">{text}</p>
        <button type="button" className="sc-mini-btn mt-4 w-full" onClick={onClose}>
          Compris
        </button>
      </section>
    </div>,
    document.body,
  );
}
